import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const TermsPage = () => {
  return (
    <>
      <SEO
        title="Conditions générales d'utilisation - Tunisie Parking"
        description="Conditions générales d'utilisation du service Tunisie Parking. Découvrez les règles, obligations et droits concernant l'utilisation de notre plateforme de parking intelligent."
        keywords="conditions générales parking tunisie, CGU parking, termes utilisation parking, légal parking tunisie"
        url="https://parking.tn/conditions-generales"
      />
      <div className="min-h-screen bg-gray-50">
      {/* Header avec navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-bold mb-4">Conditions générales d'utilisation</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </section>

      {/* Contenu */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Objet</h2>
            <p className="text-gray-700 leading-relaxed">
              Les présentes conditions générales d'utilisation (ci-après "CGU") ont pour objet de définir les conditions 
              d'accès et d'utilisation du service Tunisie Parking (ci-après "le Service") proposé par Tunisie Parking 
              (ci-après "nous", "notre" ou "la Société").
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              L'utilisation du Service implique l'acceptation pleine et entière des présentes CGU. Si vous n'acceptez pas 
              ces conditions, nous vous invitons à ne pas utiliser le Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description du service</h2>
            <p className="text-gray-700 leading-relaxed">
              Tunisie Parking est une plateforme numérique permettant aux utilisateurs de :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Gérer leurs paiements de stationnement</li>
              <li>Consulter leur historique de stationnement</li>
              <li>Gérer leurs véhicules enregistrés</li>
              <li>Effectuer des transactions de paiement via portefeuille électronique</li>
              <li>Recevoir des notifications concernant leurs tickets de stationnement</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Inscription et compte utilisateur</h2>
            <p className="text-gray-700 leading-relaxed">
              Pour utiliser le Service, vous devez créer un compte en fournissant :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Un numéro de téléphone tunisien valide</li>
              <li>Optionnellement, une adresse email</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Vous êtes responsable de la confidentialité de vos identifiants et de toutes les activités effectuées 
              depuis votre compte. Vous vous engagez à nous informer immédiatement de toute utilisation non autorisée 
              de votre compte.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Obligations de l'utilisateur</h2>
            <p className="text-gray-700 leading-relaxed">
              En utilisant le Service, vous vous engagez à :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Fournir des informations exactes, complètes et à jour</li>
              <li>Respecter la législation tunisienne en vigueur</li>
              <li>Ne pas utiliser le Service à des fins illégales ou frauduleuses</li>
              <li>Ne pas tenter d'accéder de manière non autorisée au Service</li>
              <li>Ne pas perturber le fonctionnement du Service</li>
              <li>Respecter les droits de propriété intellectuelle</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Paiements et transactions</h2>
            <p className="text-gray-700 leading-relaxed">
              Le Service permet l'utilisation d'un portefeuille électronique pour effectuer des paiements de stationnement. 
              Les transactions sont effectuées en millimes tunisiens.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Vous reconnaissez que :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Les paiements sont définitifs et non remboursables, sauf cas exceptionnels prévus par la loi</li>
              <li>Les tarifs peuvent être modifiés à tout moment, avec notification préalable</li>
              <li>Vous êtes responsable de maintenir un solde suffisant dans votre portefeuille</li>
              <li>Nous ne sommes pas responsables des erreurs de paiement dues à des informations incorrectes fournies par l'utilisateur</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Protection des données personnelles</h2>
            <p className="text-gray-700 leading-relaxed">
              Nous collectons et traitons vos données personnelles conformément à la législation tunisienne en vigueur 
              sur la protection des données à caractère personnel. Les données collectées incluent :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Numéro de téléphone</li>
              <li>Adresse email (si fournie)</li>
              <li>Informations sur vos véhicules</li>
              <li>Historique de stationnement et transactions</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Vos données sont utilisées uniquement pour la fourniture du Service et ne sont pas partagées avec des tiers, 
              sauf obligation légale ou avec votre consentement explicite.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Disponibilité du service</h2>
            <p className="text-gray-700 leading-relaxed">
              Nous nous efforçons d'assurer une disponibilité continue du Service. Cependant, nous ne pouvons garantir 
              une disponibilité à 100% en raison de :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Maintenances programmées ou d'urgence</li>
              <li>Pannes techniques</li>
              <li>Cas de force majeure</li>
              <li>Problèmes de connectivité réseau</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Nous nous réservons le droit d'interrompre temporairement l'accès au Service pour maintenance ou amélioration, 
              avec notification préalable lorsque cela est possible.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Propriété intellectuelle</h2>
            <p className="text-gray-700 leading-relaxed">
              L'ensemble des éléments du Service (textes, images, logos, logiciels, etc.) sont la propriété exclusive 
              de Tunisie Parking ou de ses partenaires et sont protégés par les lois tunisiennes et internationales 
              relatives à la propriété intellectuelle.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Toute reproduction, représentation, modification ou adaptation sans autorisation préalable est strictement 
              interdite et peut entraîner des poursuites judiciaires.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation de responsabilité</h2>
            <p className="text-gray-700 leading-relaxed">
              Dans les limites autorisées par la loi, Tunisie Parking ne pourra être tenue responsable de :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Dommages directs ou indirects résultant de l'utilisation ou de l'impossibilité d'utiliser le Service</li>
              <li>Perte de données ou d'informations</li>
              <li>Interruptions de service</li>
              <li>Erreurs ou omissions dans le contenu</li>
              <li>Utilisation frauduleuse ou non autorisée de votre compte</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modification des CGU</h2>
            <p className="text-gray-700 leading-relaxed">
              Nous nous réservons le droit de modifier les présentes CGU à tout moment. Les modifications entrent en vigueur 
              dès leur publication sur le Service. Votre utilisation continue du Service après modification des CGU vaut 
              acceptation des nouvelles conditions.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Nous vous recommandons de consulter régulièrement cette page pour prendre connaissance des éventuelles modifications.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Résiliation</h2>
            <p className="text-gray-700 leading-relaxed">
              Vous pouvez résilier votre compte à tout moment en nous contactant. Nous nous réservons également le droit 
              de suspendre ou résilier votre compte en cas de :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>Violation des présentes CGU</li>
              <li>Utilisation frauduleuse du Service</li>
              <li>Inactivité prolongée du compte</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Droit applicable et juridiction</h2>
            <p className="text-gray-700 leading-relaxed">
              Les présentes CGU sont régies par le droit tunisien. En cas de litige, et après tentative de résolution amiable, 
              les tribunaux tunisiens seront seuls compétents.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact</h2>
            <p className="text-gray-700 leading-relaxed">
              Pour toute question concernant les présentes CGU, vous pouvez nous contacter via :
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
              <li>La page de contact disponible sur notre site web</li>
              <li>Par email à l'adresse indiquée dans la section contact</li>
            </ul>
          </section>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              En utilisant le Service Tunisie Parking, vous reconnaissez avoir lu, compris et accepté les présentes 
              conditions générales d'utilisation.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
    </>
  );
};

export default TermsPage;

